import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type MarketParityParityManagerMailController from 'lumen/controllers/market/parity/parity-manager/mail';
import type { IssueType } from 'lumen/models/parity-manager/issue';
import type AjaxService from 'lumen/services/ajax';
import type { Transition } from 'lumen/services/transition';
import type { MailType } from 'lumen/utils/constants/pm-constant-values';

type MailRouteQueryParams = {
  issueId: string;
  issueType: IssueType;
  mailType: MailType;
  subscriptionId: string;
};

export interface MailTemplate {
  content: string;
  id: number;
  name: string;
  with_free_text: boolean;
}

interface MailModel {
  bccReceivers: string;
  ccReceivers: string;
  isLoading: boolean;
  mailSubject: string;
  receivers: string;
  templates: MailTemplate[];
}

interface MailResponse {
  bcc_receivers: string[];
  cc_receivers: string[];
  email_templates: MailTemplate[];
  receivers: string[];
  subject: string;
}

export default class MarketParityParityManagerMailRoute extends Route {
  queryParams = {
    subscriptionId: {},
    issueId: {},
    issueType: {},
    mailType: {},
  };

  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare transition: Services['transition'];

  beforeModel(transition: Transition): void {
    if (
      !(
        this.access.can('market.paritymanager.email.hotel.send') ||
        this.access.can('market.paritymanager.email.wholesaler.send')
      )
    ) {
      this.transition.redirectToTab(transition, 'market.parity.parity-manager');
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async model(params: MailRouteQueryParams) {
    const responseObject: MailModel = {
      isLoading: true,
      templates: [],
      mailSubject: '',
      receivers: '',
      bccReceivers: '',
      ccReceivers: '',
    };

    if (
      params.subscriptionId &&
      params.issueId &&
      params.mailType &&
      this.hotelAndMarketService.market?.id
    ) {
      // Request the correct HTML code for the escalation mail preview on init
      try {
        const response: MailResponse = await this.ajax.request(
          `${this.ajax?.host}/apigateway/v1/app/paritymanager/get_email?market_id=${this.hotelAndMarketService.market?.id}&subscription_id=${params.subscriptionId}&issue_id=${params.issueId}&issue_type=${params.issueType}&mail_type=${params.mailType}`,
          {
            method: 'GET',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
          },
        );

        if (response.email_templates) {
          responseObject.templates = response.email_templates;
        }

        if (response.receivers) {
          responseObject.receivers = response.receivers.join('; ');
        }

        if (response.cc_receivers) {
          responseObject.ccReceivers = response.cc_receivers.join('; ');
        }

        if (response.bcc_receivers) {
          responseObject.bccReceivers = response.bcc_receivers.join('; ');
        }

        responseObject.mailSubject = response.subject;

        // @ts-expect-error: Conversion of type 'Controller<unknown>' to type 'MarketParityParityManagerMailController' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
        // eslint-disable-next-line ember/no-controller-access-in-routes
        const mailController = this.controllerFor(
          'market.parity.parity-manager.mail',
        ) as MarketParityParityManagerMailController;
        mailController.mailSubject = response.subject;
        mailController.receivers = responseObject.receivers;
        mailController.ccReceivers = responseObject.ccReceivers;
        mailController.bccReceivers = responseObject.bccReceivers;
      } catch (error) {
        this.errorTracking.captureException(error);
        this.showErrorPopup();
      } finally {
        responseObject.isLoading = false;
      }
    } else {
      responseObject.isLoading = false;
    }

    return responseObject;
  }

  // @ts-expect-error: Property 'resetController' in type 'MailRoute' is not assignable to the same property in base type 'Route'.

  resetController(
    controller: MarketParityParityManagerMailController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // Clear data when the route is exiting
      controller.receivers = '';
      controller.ccReceivers = '';
      controller.bccReceivers = '';
      controller.mailSubject = '';
      controller.freeText = '';
      controller.isSendingMail = false;
    }
  }

  showErrorPopup(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      text: this.intl.t('parity_manager.violation_mail_modal.template_error'),
    });
  }
}
